// Zustand store for peer pool state

import { create } from "zustand";
import { api, type Peer, type PoolStatus, type UserInfo } from "../lib/api-client.js";

interface PeerStore {
	peers: Peer[];
	poolStatus: PoolStatus | null;
	user: UserInfo | null;
	loading: boolean;
	error: string | null;
	filter: { status?: string; search?: string };

	fetchUser: () => Promise<void>;
	fetchPeers: () => Promise<void>;
	fetchPoolStatus: () => Promise<void>;
	assignPeer: (ip: string, assignedTo: string, notes?: string) => Promise<void>;
	revokePeer: (ip: string, reason?: string) => Promise<void>;
	initializePool: () => Promise<{ inserted: number; existing: number }>;
	setFilter: (filter: Partial<PeerStore["filter"]>) => void;
}

export const usePeerStore = create<PeerStore>((set, get) => ({
	peers: [],
	poolStatus: null,
	user: null,
	loading: false,
	error: null,
	filter: {},

	fetchUser: async () => {
		try {
			const user = await api.getMe();
			set({ user });
		} catch {
			set({ user: null });
		}
	},

	fetchPeers: async () => {
		set({ loading: true, error: null });
		try {
			const { peers } = await api.listPeers(get().filter);
			set({ peers, loading: false });
		} catch (err) {
			set({ error: (err as Error).message, loading: false });
		}
	},

	fetchPoolStatus: async () => {
		try {
			const status = await api.getPoolStatus();
			set({ poolStatus: status });
		} catch (err) {
			set({ error: (err as Error).message });
		}
	},

	assignPeer: async (ip, assignedTo, notes) => {
		await api.assignPeer(ip, assignedTo, notes);
		// Refresh data
		await get().fetchPeers();
		await get().fetchPoolStatus();
	},

	revokePeer: async (ip, reason) => {
		await api.revokePeer(ip, reason);
		await get().fetchPeers();
		await get().fetchPoolStatus();
	},

	initializePool: async () => {
		const result = await api.initializePool();
		await get().fetchPeers();
		await get().fetchPoolStatus();
		return result;
	},

	setFilter: (filter) => {
		set({ filter: { ...get().filter, ...filter } });
		get().fetchPeers();
	},
}));
